"use client";

import Image from "next/image";
import { useRef } from "react";
import { useScroll, useTransform, motion, MotionValue } from "motion/react";
import type { ManifestoItem } from "@/types/database";

interface ManifestoItemComponentProps {
  readonly item: ManifestoItem;
  readonly index: number;
  readonly total: number;
  readonly scrollYProgress: MotionValue<number>;
}

function ManifestoItemComponent({
  item,
  index,
  total,
  scrollYProgress,
}: ManifestoItemComponentProps) {
  // Calculate range for this item's fading
  // The container is total * 100vh height.
  // Each item occupies 1/total of the scroll distance.
  // Item i is "active" (sticky at top) from i/total to (i+1)/total.
  // During this time, the NEXT item is sliding up over it.
  // So we fade out from 1 to 0 over this range.

  const step = 1 / total;
  const start = index * step;

  // Delay the start of the fade so text stays visible longer as next section enters
  const fadeStart = start + step * 0.4;
  const fadeEnd = start + step * 0.95;

  const isLast = index === total - 1;

  // Use a conditional transform: for the last item, keep opacity 1.
  // For others, fade out.
  const opacity = useTransform(scrollYProgress, [fadeStart, fadeEnd], [1, 0]);

  return (
    <section className="sticky top-0 h-screen w-full overflow-hidden flex items-center justify-center">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <Image
          src={item.image_url || ""}
          alt={item.title}
          fill
          className="object-cover brightness-50"
          priority={index === 0}
        />
      </div>

      {/* Content with Fade Effect */}
      <motion.div
        style={{ opacity: isLast ? 1 : opacity }}
        className="relative z-10 container px-6 md:px-12 flex flex-col items-center text-center text-white space-y-8"
      >
        <div className="space-y-4">
          <span className="text-xs uppercase tracking-[0.5em] text-white/70 block">
            0{index + 1} — Manifesto
          </span>
          <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[0.9]">
            {item.title}
          </h2>
        </div>
        <p className="max-w-2xl font-sans text-base md:text-lg font-light leading-relaxed text-white/90">
          {item.description}
        </p>
      </motion.div>
    </section>
  );
}

interface ManifestoSectionProps {
  readonly items: ManifestoItem[];
}

export function ManifestoSection({ items }: ManifestoSectionProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  return (
    <div ref={containerRef} className="relative w-full">
      {items.map((item, index) => (
        <ManifestoItemComponent
          key={item.id}
          item={item}
          index={index}
          total={items.length}
          scrollYProgress={scrollYProgress}
        />
      ))}
    </div>
  );
}
